import { useFormik } from 'formik';
import React, { useMemo } from 'react';
import * as yup from 'yup';
import { DomainTypeGq } from '../../../graphql/projects/models/DomainTypeGq';
import { projectsApiService } from '../../../graphql/projects/projectsApiService';
import VoxelDialog from '../../voxel/Dialogs/VoxelDialog';
import VoxelDialogActionSubmit from '../../voxel/Dialogs/VoxelDialogActionSubmit';
import VoxelDialogActions from '../../voxel/Dialogs/VoxelDialogActions';
import VoxelDialogContent from '../../voxel/Dialogs/VoxelDialogContent';
import DomainForm, { DomainFormState, protocolOptions } from '../Forms/DomainForm';
import { ProjectGq } from '../../../graphql/projects/models/ProjectGq';

export interface AddProjectDomainDialogProps {
  project: ProjectGq;
  onSuccess: (domainId: number) => Promise<any>;
  close: () => void;
}

const AddProjectDomainDialog: React.FC<AddProjectDomainDialogProps> = ({
  project,
  onSuccess,
  close,
}) => {
  const createDomainsMutation = projectsApiService.mutations.domains.useCreateDomains();
  const validationSchema = useMemo(
    () =>
      yup.object().shape({
        domain: yup.string().required('Required').min(1, 'Required'),
      }),
    []
  );
  const formikProps = useFormik<DomainFormState>({
    initialValues: {
      protocol: protocolOptions[0].value,
      domain: '',
      type: DomainTypeGq.PROJECT,
    },
    validationSchema: validationSchema,
    onSubmit: (values, actions) =>
      createDomainsMutation({
        projectId: project.id,
        domains: [{ domain: values.domain, protocol: values.protocol, type: values.type }]
      }).then((result) => onSuccess(result[0].id)),
  });

  return (
    <VoxelDialog
      open={true}
      onClose={close}
      closeDisabled={formikProps.isSubmitting}
      closeOnBackdrop
      maxWClass="max-w-2xl"
      title="Add project domain"
      subtitle="A project domain is required for rank based functionality, such as analyses, content plan and rank tracking."
    >
      <VoxelDialogContent classMinWidth="min-w-[600px]">
        <div className="py-2 w-full">
          <DomainForm
            protocolValue={formikProps.values.protocol}
            setProtocolValue={(value) => formikProps.setFieldValue('protocol', value)}
            domainValue={formikProps.values.domain}
            setDomainFormState={(valueFunc) => formikProps.setValues(d => { return valueFunc(d) })}
            deleteDomainValue={() => formikProps.setFieldValue('domain', '')}
            placeholder={'www.organization.com/se-sv'}
            disabled={formikProps.isSubmitting}
            helperText={formikProps.errors?.domain}
            showHelperText={formikProps.errors?.domain?.length > 0 && formikProps.submitCount > 0}
          />
        </div>
      </VoxelDialogContent>
      <VoxelDialogActions>
        <VoxelDialogActionSubmit
          title="Add"
          onClick={formikProps.submitForm}
          disabled={formikProps.isSubmitting || !formikProps.isValid}
        />
      </VoxelDialogActions>
    </VoxelDialog>
  );
};

export default AddProjectDomainDialog;
